import { LoadingOutlined } from "@ant-design/icons"
import { Spin, Tooltip, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../../../Components/Button"
import Spinner from "../../../../../Components/Spinner"
import { RoutesPaths } from "../../../../../constants/user-routes"
import Icon from "../../../../../Icon"
import { highAccessRoles } from "../../../../../Utils/permission"
import { RECEIPT_PAGE_STATE } from "../../../constants/receipt-status-mapping"
import GhostTable from "../GhostTable"

import styles from "./styles.module.less"

const { Title, Text } = Typography

export const PageStateOverlay = ({ pageState, firstEnrollBlocked, activeColumns, onEnroll }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const baRoleId = useSelector((s) => s.auth?.userInfo?.ba_role?.id)

  const isPrivileged = highAccessRoles.includes(baRoleId)
  const canEnroll = isPrivileged && !firstEnrollBlocked

  const enrollTooltip = !isPrivileged
    ? t("Allows for Owner or Administrator roles")
    : firstEnrollBlocked
      ? t("The balance should be more than $1 to enroll")
      : ""

  const handleEnroll = () => {
    if (onEnroll) return onEnroll()
    navigate(RoutesPaths.receipts._configure)
  }

  const states = {
    [RECEIPT_PAGE_STATE.NOT_ENROLLED]: {
      icon: <Icon type="receipt_bill" style={{ fontSize: 50 }} />,
      title: t("Reduce disputes with Digital Receipt"),
      description: t(
        "Show cardholders clear purchase details so they recognize legitimate charges and file fewer disputes."
      ),
      action: (
        <Tooltip title={enrollTooltip}>
          <Button
            type="primary"
            onClick={handleEnroll}
            disabled={!canEnroll}
            data-testid="deflect-user-enroll-btn"
          >
            {t("Enroll now")}
          </Button>
        </Tooltip>
      )
    },
    [RECEIPT_PAGE_STATE.PENDING]: {
      icon: <Icon type="receipt_bill" style={{ fontSize: 50 }} />,
      title: t("Enrollment in progress"),
      description: t(
        "We're processing your enrollment request. This usually takes 1 business day. We'll email you when Digital Receipt is active for your account."
      ),
      action: (
        <span className={styles.pill}>
          <Spin indicator={<LoadingOutlined spin className={styles.pillSpinner} />} size="small" />
          {t("Pending...")}
        </span>
      )
    }
  }

  const state = states[pageState]

  return (
    <div className={styles.wrap} data-testid={`deflect-user-${pageState}`}>
      <GhostTable activeColumns={activeColumns} />
      <div className={styles.overlay}>
        {state ? (
          <div className={styles.state}>
            <span className={styles.icon}>{state.icon}</span>
            <Title
              level={3}
              className={styles.title}
              data-testid={`deflect-user-${pageState}-title`}
            >
              {state.title}
            </Title>
            <Text
              type="secondary"
              className={styles.text}
              data-testid={`deflect-user-${pageState}-description`}
            >
              {state.description}
            </Text>
            <div className={styles.action}>{state.action}</div>
          </div>
        ) : (
          <div className={styles.loading}>
            <Spinner />
            <Text type="secondary">{t("No receipts found...")}</Text>
          </div>
        )}
      </div>
    </div>
  )
}
